// src/components/Modal/ModalLocalizacao.js
import React, { useEffect, useRef, useState } from 'react';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { MdMyLocation } from 'react-icons/md';
import Modal from './Modal';
import { useApi } from '../../hooks/useApi';
import { carregarGoogleMaps } from '../../utils/googleMapsUtils';
import { montarLinkLocalizacao, obterPosicaoAtual } from '../../utils/geolocalizacaoUtils';
import './ModalLocalizacao.css';

// Precisão (em metros) a partir da qual sugerimos ao cliente conferir o marcador.
// const PRECISAO_ALERTA_METROS = 100;

const descreverEndereco = (endereco) => {
  if (!endereco) return null;
  const rua = [endereco.logradouro, endereco.numero].filter(Boolean).join(', ');
  const complemento = [endereco.bairro, [endereco.cidade, endereco.uf].filter(Boolean).join('/')].filter(Boolean).join(' - ');
  return {
    titulo: rua || endereco.enderecoFormatado || 'Endereço não identificado',
    subtitulo: rua ? complemento : '',
  };
};

const ModalLocalizacao = ({ show, onClose, onConfirm }) => {
  const { fetchData } = useApi();
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  const mapaRef = useRef(null);
  const mapaInstanciaRef = useRef(null);
  const marcadorRef = useRef(null);
  const consultaRef = useRef(0);

  const [status, setStatus] = useState('localizando'); // localizando | pronto | erro
  const [erro, setErro] = useState('');
  const [posicao, setPosicao] = useState(null);
  const [posicaoGps, setPosicaoGps] = useState(null);
  const [mapaIndisponivel, setMapaIndisponivel] = useState(false);
  const [endereco, setEndereco] = useState(null);
  const [buscandoEndereco, setBuscandoEndereco] = useState(false);
  const [erroEndereco, setErroEndereco] = useState('');

  const buscarEndereco = async (latitude, longitude) => {
    const consulta = ++consultaRef.current;
    setBuscandoEndereco(true);
    setErroEndereco('');
    try {
      const resultado = await fetchDataRef.current('/TaxaEntrega/EnderecoPorCoordenadas', { latitude, longitude });
      if (consulta !== consultaRef.current) return;
      setEndereco(resultado || null);
      if (!resultado?.logradouro) {
        setErroEndereco('Não encontramos a rua deste ponto. Você poderá completar o endereço manualmente.');
      }
    } catch (error) {
      if (consulta !== consultaRef.current) return;
      setEndereco(null);
      setErroEndereco('Não foi possível buscar o endereço deste ponto. Você poderá preencher manualmente.');
    } finally {
      if (consulta === consultaRef.current) setBuscandoEndereco(false);
    }
  };

  const moverPara = (latitude, longitude) => {
    setPosicao((anterior) => ({ ...anterior, latitude, longitude }));
    buscarEndereco(latitude, longitude);
  };

  const localizar = async () => {
    const consulta = ++consultaRef.current;
    setStatus('localizando');
    setErro('');
    setEndereco(null);
    setErroEndereco('');
    try {
      const atual = await obterPosicaoAtual();
      if (consulta !== consultaRef.current) return;
      setPosicaoGps(atual);
      setPosicao(atual);
      setStatus('pronto');
      buscarEndereco(atual.latitude, atual.longitude);
    } catch (error) {
      if (consulta !== consultaRef.current) return;
      setErro(error.message);
      setStatus('erro');
    }
  };

  // Ao abrir solicita a localização; ao fechar descarta mapa e consultas pendentes.
  useEffect(() => {
    if (show) {
      setMapaIndisponivel(false);
      localizar();
    } else {
      consultaRef.current += 1;
      mapaInstanciaRef.current = null;
      marcadorRef.current = null;
      setPosicao(null);
      setPosicaoGps(null);
    }
  }, [show]);

  // Monta o mapa com o marcador arrastável depois que a posição do GPS chega.
  useEffect(() => {
    if (!show || status !== 'pronto' || !posicaoGps || mapaIndisponivel) return undefined;

    let cancelado = false;
    carregarGoogleMaps()
      .then((maps) => {
        if (cancelado || !mapaRef.current) return;
        const centro = { lat: posicaoGps.latitude, lng: posicaoGps.longitude };

        if (mapaInstanciaRef.current) {
          mapaInstanciaRef.current.panTo(centro);
          marcadorRef.current?.setPosition(centro);
          return;
        }

        const mapa = new maps.Map(mapaRef.current, {
          center: centro,
          zoom: 18,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
          gestureHandling: 'greedy',
        });
        const marcador = new maps.Marker({
          position: centro,
          map: mapa,
          draggable: true,
          title: 'Local da entrega',
          animation: maps.Animation?.DROP,
        });

        if (posicaoGps.precisao && posicaoGps.precisao < 1000) {
          new maps.Circle({
            map: mapa,
            center: centro,
            radius: posicaoGps.precisao,
            clickable: false,
            strokeColor: '#1a73e8',
            strokeOpacity: 0.35,
            strokeWeight: 1,
            fillColor: '#1a73e8',
            fillOpacity: 0.08,
          });
        }

        marcador.addListener('dragend', (evento) => moverPara(evento.latLng.lat(), evento.latLng.lng()));
        mapa.addListener('click', (evento) => {
          marcador.setPosition(evento.latLng);
          moverPara(evento.latLng.lat(), evento.latLng.lng());
        });

        mapaInstanciaRef.current = mapa;
        marcadorRef.current = marcador;
      })
      .catch(() => {
        if (!cancelado) setMapaIndisponivel(true);
      });

    return () => { cancelado = true; };
  }, [show, status, posicaoGps, mapaIndisponivel]);

  const voltarParaMinhaLocalizacao = () => {
    if (!posicaoGps) return;
    const centro = { lat: posicaoGps.latitude, lng: posicaoGps.longitude };
    mapaInstanciaRef.current?.panTo(centro);
    marcadorRef.current?.setPosition(centro);
    moverPara(posicaoGps.latitude, posicaoGps.longitude);
  };

  const confirmar = () => {
    if (!posicao) return;
    onConfirm({
      endereco,
      latitude: posicao.latitude,
      longitude: posicao.longitude,
      localizacao: endereco?.localizacao || montarLinkLocalizacao(posicao.latitude, posicao.longitude),
    });
  };

  const descricao = descreverEndereco(endereco);
  // const precisaoBaixa = posicaoGps?.precisao > PRECISAO_ALERTA_METROS;

  return (
    <Modal
      show={show}
      onClose={onClose}
      titulo="Sua localização"
      className="endereco-modal localizacao-modal"
    >
      <p className="endereco-modal-description">
        Confira o ponto no mapa. Se precisar, arraste o marcador ou toque no local exato da entrega.
      </p>

      <div className="localizacao-mapa-area">
        {status === 'localizando' && (
          <div className="localizacao-estado" role="status">
            <span className="localizacao-spinner" aria-hidden="true" />
            <p>Obtendo sua localização...</p>
            <small>Se o navegador perguntar, permita o acesso à localização.</small>
          </div>
        )}

        {status === 'erro' && (
          <div className="localizacao-estado erro" role="alert">
            <HiOutlineLocationMarker size={34} aria-hidden="true" />
            <p>{erro}</p>
            <button type="button" className="localizacao-tentar" onClick={localizar}>
              Tentar novamente
            </button>
          </div>
        )}

        {status === 'pronto' && mapaIndisponivel && (
          <div className="localizacao-estado">
            <HiOutlineLocationMarker size={34} aria-hidden="true" />
            <p>Localização encontrada.</p>
            <small>Não foi possível exibir o mapa agora, mas você pode confirmar o endereço abaixo.</small>
          </div>
        )}

        {status === 'pronto' && !mapaIndisponivel && (
          <>
            <div ref={mapaRef} className="localizacao-mapa" role="region" aria-label="Mapa com a sua localização" />
            <button
              type="button"
              className="localizacao-recentrar"
              onClick={voltarParaMinhaLocalizacao}
              aria-label="Voltar para minha localização"
              title="Voltar para minha localização"
            >
              <MdMyLocation size={22} aria-hidden="true" />
            </button>
          </>
        )}
      </div>

      {status === 'pronto' && (
        <div className="localizacao-endereco" aria-live="polite">
          <span className="localizacao-endereco-icone" aria-hidden="true">
            <HiOutlineLocationMarker size={22} />
          </span>
          <div>
            {buscandoEndereco ? (
              <strong>Buscando endereço...</strong>
            ) : (
              <>
                <strong>{descricao?.titulo || 'Endereço não identificado'}</strong>
                {/* Bairro e cidade não são exibidos: a localização preenche só rua e número */}
                {/* {descricao?.subtitulo && <span>{descricao.subtitulo}</span>} */}
              </>
            )}
            {!buscandoEndereco && erroEndereco && <span className="localizacao-aviso">{erroEndereco}</span>}
            {/* Aviso de precisão desativado
            {precisaoBaixa && (
              <span className="localizacao-aviso">
                Precisão aproximada de {Math.round(posicaoGps.precisao)} m. Confira o marcador no mapa.
              </span>
            )} */}
          </div>
        </div>
      )}

      <div className="modal-texto-buttons">
        <button type="button" className="modal-texto-button-secondary" onClick={onClose}>
          Fechar
        </button>
        <button
          type="button"
          className="modal-texto-button"
          onClick={confirmar}
          disabled={status !== 'pronto' || buscandoEndereco}
        >
          Confirmar
        </button>
      </div>
    </Modal>
  );
};

export default ModalLocalizacao;
